import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container, Typography, Grid, Card, CardContent, Box, Button,
  Chip, IconButton, Dialog, DialogTitle, DialogContent, 
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  List, ListItem, ListItemIcon, ListItemText, Divider, Alert,
  CircularProgress, Tabs, Tab, CardActions, Avatar, LinearProgress,
  Accordion, AccordionSummary, AccordionDetails, Fab, Tooltip, Stack
} from '@mui/material';
import {
  Assignment, LocationOn, Schedule, TrendingUp, AttachMoney,
  AccessTime, Navigation, Phone, Email, PhotoCamera,
  Videocam, Build, FilterList, Search, PlayArrow, Pause, Stop,
  CheckBox, Cancel, Warning, Info, Verified, Edit, Add, Route,
  MyLocation, Timer, CameraAlt, Upload, FileDownload, ExpandMore,
  DirectionsCar, Speed, NearMe, Refresh as RefreshIcon, Message, Description
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import DialogCloseButton from '../components/common/DialogCloseButton';
import simpleVerificationService from '../services/simpleVerificationService';
import verifierService from '../services/verifierService';
import contractorVerificationService from '../services/contractorVerificationService';
import VerifierIdentityBanner from '../components/verifiers/VerifierIdentityBanner';
import messagingService from '../services/messagingService';
import verifierAgreementService from '../services/verifierAgreementService';
import { verifier as verifierColor, status } from '../theme/colors';

// Work in these states is finished — excluded by the 'Active' group filter.
const TERMINAL_STATUSES = ['completed', 'approved', 'cancelled', 'rejected'];
// Regular verifications that count as finished work.
const COMPLETED_STATUSES = ['completed', 'approved'];
// Contractor verification files still needing verifier action.
const ACTIVE_FILE_STATUSES = ['assigned', 'in_progress', 'changes_requested'];

const VerifierAssignmentsPage = ({ showHistoryTab = true }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // All verifications — including Property Checks — use the structured 5-step
  // workflow. Its submit endpoint runs the AI photo enrichment + photo-backed
  // PDF pipeline for property-check bookings.
  const startVerification = (assignment) => {
    if (!assignment) return;
    navigate(`/verifier/verify/${assignment.id}`);
  };

  // Completed work links straight to the final report — the same view the
  // client receives.
  const viewReport = (verificationId) => {
    navigate(`/verifier/simple-verifications/${verificationId}/report`);
  };

  // Agreement gate
  const [agreementAccepted, setAgreementAccepted] = useState(null); // null = loading, true/false = known
  const [agreementApplicable, setAgreementApplicable] = useState(false);

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [assignmentDetailsDialog, setAssignmentDetailsDialog] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [verificationDialog, setVerificationDialog] = useState(false);
  const [filterDialog, setFilterDialog] = useState(false);
  const [detailsTab, setDetailsTab] = useState(0);

  // Filter states - initialize from URL params if present. There are no
  // Active/Completed tabs anymore — 'active' and 'completed' are Status
  // filter values like any other (old ?tab=completed links map to the
  // 'completed' filter).
  const [statusFilter, setStatusFilter] = useState(() => {
    const urlStatus = searchParams.get('status');
    if (urlStatus) return urlStatus;
    if (searchParams.get('tab') === 'completed') return 'completed';
    return 'all';
  });
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get('priority') || 'all');
  const [locationFilter, setLocationFilter] = useState(searchParams.get('location') || 'all');
  
  // Data states
  const [assignments, setAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [contractorFileAssignments, setContractorFileAssignments] = useState([]);
  const [verifierProfile, setVerifierProfile] = useState(null);

  const loadAssignments = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Fetch from new simple_verifications system
      // Always fetch the FULL set (no server-side status filter): the tab
      // badges must be stable totals, and status filtering happens client-side
      // alongside priority/location. Filtering server-side made the badges
      // recompute from the filtered response and shift with the dropdown.
      const response = await simpleVerificationService.getMyVerifications();

      if (response.success && response.data) {
        // Map new system data to old structure for compatibility
        const parseJson = (v, fallback) => {
          if (v == null) return fallback;
          if (typeof v !== 'string') return v;
          try { return JSON.parse(v); } catch { return fallback; }
        };
        // Dedupe by id: the backend LEFT JOINs verification_requests, which can
        // fan out one verification into multiple rows and inflate the counts.
        const seenIds = new Set();
        const uniqueRows = response.data.filter(v => {
          if (seenIds.has(v.id)) return false;
          seenIds.add(v.id);
          return true;
        });
        const mappedAssignments = uniqueRows.map(verif => {
          const requestLocation = parseJson(verif.requestLocation, null);
          const requestScheduling = parseJson(verif.requestScheduling, {});
          const gpsLocation = parseJson(verif.gpsLocation, null);
          return ({
          id: verif.id,
          milestone: verif.title,
          title: verif.title,
          description: verif.description,
          // Legacy rows can carry a NULL status; render them as pending so
          // .replace()/.toUpperCase() on the card never crash the page.
          status: verif.status || 'pending',
          priority: verif.priority || 'normal', // Default to normal if not set
          urgency: verif.urgency || 'medium', // Default to medium if not set
          verificationType: verif.verificationType,
          source: verif.source,
          verificationRequestId: verif.verificationRequestId,
          packageSlug: verif.packageSlug,
          propertyCheckPackage: verif.packageDisplayName
            ? {
                displayName: verif.packageDisplayName,
                turnaroundHours: verif.packageTurnaroundHours,
                scopeTemplateId: verif.packageScopeTemplateId
              }
            : null,
          focusAreas: Array.isArray(verif.focusAreas) ? verif.focusAreas : [],
          tier: verif.tier,
          paymentAmount: verif.cost,
          duration: verif.verificationDuration,
          // Property checks store location/scheduling on the verification_request
          // (requestLocation/requestScheduling). The simple_verifications shadow
          // row has no schedule column and a null gpsLocation, so prefer the
          // request fields and fall back to the GPS proof for milestone work.
          scheduledDate: requestScheduling.requestedDate || verif.scheduledDate || null,
          scheduledTime: requestScheduling.preferredStartTime || verif.scheduledTime || null,
          scheduleFlexibility: requestScheduling.flexibility || null,
          location: requestLocation || gpsLocation || { area: '', address: '' },
          projectId: verif.projectId,
          projectTitle: verif.projectTitle || (verif.projectId ? `Project ${verif.projectId}` : 'Property Check'),
          clientId: verif.clientId,
          requirements: verif.checklist ?
            (typeof verif.checklist === 'string' ? JSON.parse(verif.checklist) : verif.checklist) :
            [],
          documents: []
          });
        });

        setAssignments(mappedAssignments);
        setFilteredAssignments(mappedAssignments);

        if (!mappedAssignments.length) {
          setSuccess('No verification assignments available yet. We will notify you when new work arrives.');
        } else {
          setSuccess('');
        }
      } else {
        setAssignments([]);
        setFilteredAssignments([]);
        setSuccess('No verification assignments available yet. We will notify you when new work arrives.');
      }

      try {
        // Unfiltered for the same reason as above — status is applied client-side.
        const contractorFiles = await contractorVerificationService.listVerifierAssignments({});
        setContractorFileAssignments(contractorFiles.data?.assignments || []);
      } catch (contractorFileErr) {
        console.error('Error loading contractor verification files:', contractorFileErr);
        setContractorFileAssignments([]);
      }
    } catch (err) {
      console.error('Error loading assignments:', err);
      setError(err.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Load verifier profile to get tier information
  const loadVerifierProfile = useCallback(async () => {
    if (!user?.id) return;

    try {
      const profile = await verifierService.getMyProfile();
      setVerifierProfile(profile);
    } catch (err) {
      console.error('Error fetching verifier profile:', err);
      // Don't show error to user, just log it
    }
  }, [user?.id]);

  useEffect(() => {
    loadVerifierProfile();
  }, [loadVerifierProfile]);

  // Load assignments when filters change (including URL params)
  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  // Set page title
  useEffect(() => {
    document.title = 'My Work';
  }, []);

  // Check agreement acceptance + applicability
  useEffect(() => {
    verifierAgreementService.getAgreement()
      .then((data) => {
        setAgreementAccepted(data.accepted);
        setAgreementApplicable(data.applicable === true);
      })
      .catch(() => setAgreementAccepted(true)); // fail-open: don't block on service error
  }, []);

  // Keep filter state in sync with the URL after mount (back/forward,
  // query-only navigation). An explicit ?status= wins over a legacy
  // ?tab=completed so a stale tab param can't pin the dropdown.
  useEffect(() => {
    const urlStatus = searchParams.get('status');
    if (urlStatus) {
      setStatusFilter(urlStatus);
    } else if (searchParams.get('tab') === 'completed') {
      setStatusFilter('completed');
    } else {
      setStatusFilter('all');
    }
  }, [searchParams]);

  // Get unique locations from assignments
  const uniqueLocations = [...new Set(assignments.map(a => a.location?.area).filter(Boolean))].sort();

  // Write the current filter selections to the URL (dropping any legacy tab
  // param) so refresh/back keeps them and the URL-sync effect agrees.
  const syncFiltersToUrl = (status, priority, location) => {
    const params = new URLSearchParams(searchParams);
    params.delete('tab');
    if (status === 'all') params.delete('status'); else params.set('status', status);
    if (priority === 'all') params.delete('priority'); else params.set('priority', priority);
    if (location === 'all') params.delete('location'); else params.set('location', location);
    navigate(`?${params.toString()}`, { replace: true });
  };

  // One status vocabulary for the whole list. 'all' shows EVERYTHING;
  // 'active' and 'completed' are group filters alongside the raw statuses.
  const matchesStatus = (status, isFile) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') {
      return isFile
        ? ACTIVE_FILE_STATUSES.includes(status)
        : !TERMINAL_STATUSES.includes(status);
    }
    if (statusFilter === 'completed') {
      // A submitted contractor file is finished work from the verifier's side.
      return isFile ? status === 'submitted' : COMPLETED_STATUSES.includes(status);
    }
    return status === statusFilter;
  };

  // Apply status, location and priority filters on the frontend.
  useEffect(() => {
    let filtered = assignments.filter(a => matchesStatus(a.status, false));

    if (priorityFilter !== 'all') {
      filtered = filtered.filter(a => a.priority === priorityFilter);
    }

    if (locationFilter !== 'all') {
      filtered = filtered.filter(a => a.location?.area === locationFilter);
    }

    setFilteredAssignments(filtered);
  }, [assignments, locationFilter, priorityFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Contractor files, honoring the status filter. Files have no
  // priority/location attributes, so those filters hide them (a file can
  // never match "High Priority" or a specific location).
  const visibleContractorFiles = contractorFileAssignments.filter(f =>
    matchesStatus(f.status, true)
    && priorityFilter === 'all'
    && locationFilter === 'all'
  );

  // Counts for the one-tap status pills — always the full totals, never the
  // filtered view, so the pills are a stable map of the whole queue.
  const countAll = assignments.length + contractorFileAssignments.length;
  const countActive =
    assignments.filter(a => !TERMINAL_STATUSES.includes(a.status)).length +
    contractorFileAssignments.filter(f => ACTIVE_FILE_STATUSES.includes(f.status)).length;
  const countCompleted =
    assignments.filter(a => COMPLETED_STATUSES.includes(a.status)).length +
    contractorFileAssignments.filter(f => f.status === 'submitted').length;

  // The single most urgent piece of work: earliest due/scheduled active item
  // (undated work sorts last). Drives the "Up next" spotlight.
  const upNext = (() => {
    const candidates = [
      ...contractorFileAssignments
        .filter(f => ACTIVE_FILE_STATUSES.includes(f.status))
        .map(f => ({ kind: 'file', due: f.due_at ? new Date(f.due_at).getTime() : Infinity, item: f })),
      ...assignments
        .filter(a => !TERMINAL_STATUSES.includes(a.status))
        .map(a => ({ kind: 'job', due: a.scheduledDate ? new Date(a.scheduledDate).getTime() : Infinity, item: a }))
    ];
    candidates.sort((x, y) => x.due - y.due);
    return candidates[0] || null;
  })();
  const showUpNext = Boolean(upNext)
    && (statusFilter === 'all' || statusFilter === 'active')
    && priorityFilter === 'all' && locationFilter === 'all';

  const statusLabel = (s) => {
    const label = (s || 'pending').replaceAll('_', ' ');
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  // Granular statuses offered in the refine dropdown; 'all'/'active'/'completed'
  // live on the pills instead.
  const GRANULAR_STATUSES = ['assigned', 'in_progress', 'pending', 'submitted', 'changes_requested', 'disputed'];

  const getStatusColor = (status) => {
    const colors = {
      'assigned': 'primary',
      'in_progress': 'warning',
      'completed': 'success',
      'approved': 'success',
      'submitted': 'success',
      'changes_requested': 'warning',
      'disputed': 'error',
      'pending': 'info',
      'cancelled': 'error'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'error',
      'normal': 'primary',
      'low': 'info'
    };
    return colors[priority] || 'default';
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      'high': 'error',
      'medium': 'warning',
      'low': 'success'
    };
    return colors[urgency] || 'default';
  };

  const formatCurrency = (amount) => {
    const value = Number(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(isNaN(value) ? 0 : value);
  };

  const getLocationLabel = (location) => {
    const area = location?.area || location?.address || location?.city || '';
    return area && area !== 'Location TBD' ? area : 'Location not set';
  };

  const FLEXIBILITY_LABELS = {
    flexible: 'Flexible — owner has no fixed date',
    urgent_same_day: 'Urgent — same-day',
    specific_date: 'Specific date requested',
  };

  const getScheduleLabel = (date, flexibility) => {
    if (date && date !== 'TBD') {
      return new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }
    if (flexibility && FLEXIBILITY_LABELS[flexibility]) return FLEXIBILITY_LABELS[flexibility];
    return 'Schedule not set';
  };

  const handleStartVerification = (assignment) => {
    setSelectedAssignment(assignment);
    setVerificationDialog(true);
  };

  const handleViewAssignmentDetails = (assignment) => {
    setSelectedAssignment(assignment);
    setDetailsTab(0); // Reset to first tab
    setAssignmentDetailsDialog(true);
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setPriorityFilter('all');
    setLocationFilter('all');
    // Clear URL parameters
    navigate('/verifier/my-work', { replace: true });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading assignments...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <VerifierIdentityBanner />
      {agreementApplicable && agreementAccepted === false && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={() => navigate('/verifier/agreement')}
              sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              Read & Sign
            </Button>
          }
        >
          <strong>Action required:</strong> You must read and sign the Field Verifier Agreement before taking on contractor verification work.
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadAssignments}>
              Retry
            </Button>
          }
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {/* Queue toolbar: one-tap status pills + compact refinements. */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[
            { value: 'all', label: 'All', count: countAll },
            { value: 'active', label: 'Active', count: countActive },
            { value: 'completed', label: 'Completed', count: countCompleted }
          ].map((pill) => {
            const selected = statusFilter === pill.value;
            return (
              <Chip
                key={pill.value}
                label={`${pill.label} · ${pill.count}`}
                clickable
                onClick={() => {
                  setStatusFilter(pill.value);
                  syncFiltersToUrl(pill.value, priorityFilter, locationFilter);
                }}
                sx={{
                  fontWeight: 700,
                  px: 0.5,
                  bgcolor: selected ? verifierColor.primary : 'transparent',
                  color: selected ? 'white' : 'text.primary',
                  border: '1.5px solid',
                  borderColor: selected ? verifierColor.primary : 'divider',
                  '&:hover': { bgcolor: selected ? verifierColor.primary : `${verifierColor.primary}14` }
                }}
              />
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: { sm: 'auto' } }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={GRANULAR_STATUSES.includes(statusFilter) ? statusFilter : ''}
              onChange={(e) => {
                const next = e.target.value || 'all';
                setStatusFilter(next);
                syncFiltersToUrl(next, priorityFilter, locationFilter);
              }}
              label="Status"
            >
              <MenuItem value="">Any status</MenuItem>
              {GRANULAR_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value);
                syncFiltersToUrl(statusFilter, e.target.value, locationFilter);
              }}
              label="Priority"
            >
              <MenuItem value="all">All priorities</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Location</InputLabel>
            <Select
              value={locationFilter}
              onChange={(e) => {
                setLocationFilter(e.target.value);
                syncFiltersToUrl(statusFilter, priorityFilter, e.target.value);
              }}
              label="Location"
            >
              <MenuItem value="all">All locations</MenuItem>
              {uniqueLocations.map((location) => (
                <MenuItem key={location} value={location}>
                  {location}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(statusFilter !== 'all' || priorityFilter !== 'all' || locationFilter !== 'all') && (
            <Button size="small" onClick={clearFilters}>
              Clear
            </Button>
          )}
        </Box>
      </Box>

      {/* Filter summary — only when something is filtered out. */}
      {(statusFilter !== 'all' || priorityFilter !== 'all' || locationFilter !== 'all') && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Showing {filteredAssignments.length + visibleContractorFiles.length} of {countAll} items
        </Typography>
      )}

      {/* Up next: the earliest-due active job, spotlighted. The item also
          appears in the list below — this strip is a pointer, not a copy. */}
      {showUpNext && (
        <Box
          sx={{
            mb: 3,
            px: { xs: 2, sm: 3 },
            py: 2,
            borderRadius: 2.5,
            border: `1.5px solid ${verifierColor.primary}55`,
            bgcolor: `${verifierColor.primary}0d`,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="overline" sx={{ color: verifierColor.primary, fontWeight: 800, letterSpacing: 1.2 }}>
              Up next
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {upNext.kind === 'file'
                ? (upNext.item.company_name || [upNext.item.first_name, upNext.item.last_name].filter(Boolean).join(' ') || upNext.item.contractor_email)
                : (upNext.item.title || 'Verification task')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {upNext.kind === 'file'
                ? ['Contractor file', upNext.item.due_at ? `due ${new Date(upNext.item.due_at).toLocaleDateString()}` : null]
                    .filter(Boolean).join(' · ')
                : [
                    formatCurrency(upNext.item.paymentAmount),
                    getLocationLabel(upNext.item.location),
                    upNext.item.scheduledDate ? new Date(upNext.item.scheduledDate).toLocaleDateString() : null
                  ].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<PlayArrow />}
            sx={{ bgcolor: verifierColor.primary, '&:hover': { bgcolor: verifierColor.primary } }}
            onClick={() =>
              upNext.kind === 'file'
                ? navigate(`/verifier/contractor-files/${upNext.item.id}`)
                : startVerification(upNext.item)}
          >
            {upNext.kind === 'file'
              ? 'Continue file'
              : upNext.item.status === 'in_progress' ? 'Continue' : 'Start'}
          </Button>
        </Box>
      )}

      {/* Unified work list: contractor files and verification assignments share
          one grid, one card size, and one status-chip style. Slim group
          headers separate the two kinds of work — vetting jobs assigned by
          the Eqaya admin vs verification jobs paid for by clients. */}
      <Grid container spacing={3}>
        {visibleContractorFiles.length > 0 && (
          <Grid item xs={12} sx={{ pb: 0 }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Contractor verification files ({visibleContractorFiles.length})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Admin-assigned contractor vetting — field evidence packs and recommendations.
            </Typography>
          </Grid>
        )}
        {visibleContractorFiles.map((file) => (
          <Grid item xs={12} sm={6} lg={4} key={`file-${file.id}`}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2.5,
                transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
                '&:hover': {
                  borderColor: `${verifierColor.primary}88`,
                  boxShadow: 2,
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                  {file.company_name || [file.first_name, file.last_name].filter(Boolean).join(' ') || file.contractor_email}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75, mb: 1.5 }}>
                  <Chip
                    label={statusLabel(file.status)}
                    color={getStatusColor(file.status)}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip label="Contractor file" size="small" variant="outlined" color="secondary" />
                </Box>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {[file.phone_number, file.contractor_email].filter(Boolean).join(' · ')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                  <Chip size="small" variant="outlined" label={file.required_site_visit_count > 0 ? `${file.required_site_visit_count} site visits` : 'Site visits optional'} />
                  <Chip
                    size="small"
                    variant="outlined"
                    color={file.gps_proof_mode === 'admin_override' ? 'warning' : 'default'}
                    label={file.gps_proof_mode === 'admin_override' ? 'GPS override' : 'GPS required'}
                  />
                  {file.due_at && (
                    <Chip size="small" variant="outlined" label={`Due ${new Date(file.due_at).toLocaleDateString()}`} />
                  )}
                </Box>
                {file.assignment_notes && (
                  <Typography variant="body2" sx={{ mt: 1 }}>{file.assignment_notes}</Typography>
                )}
              </CardContent>
              <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
                <Button
                  fullWidth
                  size="small"
                  variant={ACTIVE_FILE_STATUSES.includes(file.status) ? 'contained' : 'outlined'}
                  startIcon={ACTIVE_FILE_STATUSES.includes(file.status) ? <PlayArrow /> : <Description />}
                  onClick={() => navigate(`/verifier/contractor-files/${file.id}`)}
                >
                  {ACTIVE_FILE_STATUSES.includes(file.status)
                    ? 'Continue file'
                    : file.status === 'submitted' ? 'View submitted pack' : 'View file'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
        {filteredAssignments.length > 0 && visibleContractorFiles.length > 0 && (
          <Grid item xs={12} sx={{ pb: 0, mt: 1 }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Client verification jobs ({filteredAssignments.length})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Property checks and project verifications requested by clients.
            </Typography>
          </Grid>
        )}
        {filteredAssignments.map((assignment) => (
          <Grid item xs={12} sm={6} lg={4} key={assignment.id}>
            <Card
              elevation={0}
              onClick={COMPLETED_STATUSES.includes(assignment.status) ? () => viewReport(assignment.id) : undefined}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2.5,
                cursor: COMPLETED_STATUSES.includes(assignment.status) ? 'pointer' : 'default',
                transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
                '&:hover': {
                  borderColor: `${verifierColor.primary}88`,
                  boxShadow: 2,
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                {/* Title with the fee beside it — the two facts a verifier
                    scans for first. */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, flex: 1 }}>
                    {(() => {
                      const milestone = assignment.milestone || assignment.title || 'Verification Task';
                      const cleanedMilestone = milestone.replace(/\s*-\s*Marketplace\s+\d+$/, '').trim();
                      return cleanedMilestone || milestone;
                    })()}
                  </Typography>
                  <Typography variant="subtitle1" color="success.main" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {formatCurrency(assignment.paymentAmount)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75, mb: 1.5 }}>
                  <Chip
                    label={statusLabel(assignment.status)}
                    color={getStatusColor(assignment.status)}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  {assignment.source === 'property_check' && (
                    <Chip
                      label={assignment.propertyCheckPackage?.displayName || 'Property check'}
                      size="small"
                      variant="outlined"
                      color="secondary"
                    />
                  )}
                  {assignment.priority === 'high' && (
                    <Chip label="High priority" color="error" size="small" variant="outlined" />
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn sx={{ fontSize: 18 }} color="action" />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {getLocationLabel(assignment.location)}
                      {assignment.location?.distance ? ` (${assignment.location.distance}km)` : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Schedule sx={{ fontSize: 18 }} color="action" />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {getScheduleLabel(assignment.scheduledDate, assignment.scheduleFlexibility)}
                    </Typography>
                  </Box>
                  {assignment.source === 'property_check' && (
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                      {assignment.propertyCheckPackage?.turnaroundHours && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${assignment.propertyCheckPackage.turnaroundHours}h turnaround`}
                        />
                      )}
                      {(assignment.focusAreas || []).slice(0, 3).map((area) => (
                        <Chip key={area} size="small" variant="outlined" label={area} />
                      ))}
                    </Box>
                  )}
                </Box>
              </CardContent>

              {/* One primary action; the rest collapse into icons. */}
              <CardActions sx={{ pt: 0, px: 2, pb: 2, gap: 1 }}>
                {(assignment.status === 'assigned' || assignment.status === 'accepted' || assignment.status === 'in_progress') && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PlayArrow />}
                    sx={{ flex: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      startVerification(assignment);
                    }}
                  >
                    {assignment.status === 'in_progress' ? 'Continue' : 'Start'}
                  </Button>
                )}
                {COMPLETED_STATUSES.includes(assignment.status) && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Description />}
                    sx={{ flex: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      viewReport(assignment.id);
                    }}
                  >
                    View report
                  </Button>
                )}
                <Tooltip title="View details">
                  <IconButton
                    size="small"
                    aria-label="View details"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewAssignmentDetails(assignment);
                    }}
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                  >
                    <Info fontSize="small" />
                  </IconButton>
                </Tooltip>
                {assignment.clientId && (
                  <Tooltip title="Message client">
                    <IconButton
                      size="small"
                      aria-label="Message client"
                      sx={{ border: '1px solid', borderColor: 'divider' }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const response = await messagingService.getOrCreateConversation(assignment.clientId);
                          const conversationId = response.data?.id || response.id;
                          navigate(`/messages/${conversationId}`);
                        } catch (err) {
                          console.error('Error creating conversation:', err);
                        }
                      }}
                    >
                      <Message fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* No Results */}
      {filteredAssignments.length === 0 && visibleContractorFiles.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No assignments found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your filters or check back later for new assignments.
            </Typography>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={clearFilters}>
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Assignment Details Dialog - Revamped with Tabs */}
      <Dialog
        open={assignmentDetailsDialog}
        onClose={() => setAssignmentDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setAssignmentDetailsDialog(false)} />
        {selectedAssignment && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {(() => {
                      const milestone = selectedAssignment.milestone || selectedAssignment.title || 'Verification Task';
                      const cleanedMilestone = milestone.replace(/\s*-\s*Marketplace\s+\d+$/, '').trim();
                      return cleanedMilestone || milestone;
                    })()}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip
                      label={selectedAssignment.status.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(selectedAssignment.status)}
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {selectedAssignment.projectTitle}
                    </Typography>
                  </Box>
                </Box>
                {(selectedAssignment.status === 'assigned' || selectedAssignment.status === 'accepted' || selectedAssignment.status === 'in_progress') && (
                  <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={() => {
                      setAssignmentDetailsDialog(false);
                      startVerification(selectedAssignment);
                    }}
                    sx={{ ml: 2 }}
                  >
                    {selectedAssignment.status === 'in_progress' ? 'Continue' : 'Start'}
                  </Button>
                )}
                {(selectedAssignment.status === 'completed' || selectedAssignment.status === 'approved' || selectedAssignment.status === 'disputed') && (
                  <Button
                    variant="contained"
                    startIcon={<Description />}
                    onClick={() => {
                      setAssignmentDetailsDialog(false);
                      viewReport(selectedAssignment.id);
                    }}
                    sx={{ ml: 2 }}
                  >
                    View Report
                  </Button>
                )}
              </Box>

              {/* Tabs */}
              <Tabs
                value={detailsTab}
                onChange={(e, newValue) => setDetailsTab(newValue)}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Overview" />
                <Tab label="Location" />
                <Tab label="Requirements" />
                {selectedAssignment.documents && selectedAssignment.documents.length > 0 && (
                  <Tab label="Documents" />
                )}
              </Tabs>
            </DialogTitle>

            <DialogContent dividers sx={{ minHeight: 300 }}>
              {/* Tab 0: Overview */}
              {detailsTab === 0 && (
                <Box>
                  {/* Quick Stats */}
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: 2 }}>
                        <AttachMoney sx={{ fontSize: 32, color: 'success.main' }} />
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                          {formatCurrency(selectedAssignment.paymentAmount)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Payment</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.50', borderRadius: 2 }}>
                        <Timer sx={{ fontSize: 32, color: 'primary.main' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {selectedAssignment.duration ? `${selectedAssignment.duration}m` :
                           selectedAssignment.verificationDuration ? `${selectedAssignment.verificationDuration}m` :
                           selectedAssignment.estimatedHours ? `${selectedAssignment.estimatedHours}h` : 'TBD'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Duration</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50', borderRadius: 2 }}>
                        <Schedule sx={{ fontSize: 32, color: 'warning.main' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                          {selectedAssignment.scheduledDate || 'Not scheduled'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Schedule</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'error.50', borderRadius: 2 }}>
                        <Warning sx={{ fontSize: 32, color: 'error.main' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                          {selectedAssignment.urgency.toUpperCase()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Urgency</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Description */}
                  {selectedAssignment.source === 'property_check' && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Property Check Scope
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Chip
                          color="secondary"
                          label={selectedAssignment.propertyCheckPackage?.displayName || 'Property Check'}
                        />
                        {selectedAssignment.propertyCheckPackage?.turnaroundHours && (
                          <Chip
                            variant="outlined"
                            label={`${selectedAssignment.propertyCheckPackage.turnaroundHours}h turnaround`}
                          />
                        )}
                      </Box>
                      {selectedAssignment.focusAreas?.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                          {selectedAssignment.focusAreas.map((area) => (
                            <Chip key={area} size="small" variant="outlined" label={area} />
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Description */}
                  {selectedAssignment.description && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Description
                      </Typography>
                      <Typography variant="body1">
                        {selectedAssignment.description}
                      </Typography>
                    </Box>
                  )}

                  {/* Quick Actions */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Route />}
                      onClick={() => {
                        if (selectedAssignment.location?.address) {
                          window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedAssignment.location.address)}`, '_blank');
                        }
                      }}
                    >
                      Get Directions
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Message />}
                      onClick={() => {
                        if (selectedAssignment.projectId) {
                          navigate(`/messages?projectId=${selectedAssignment.projectId}&clientId=${selectedAssignment.clientId}`);
                        } else {
                          navigate('/messages');
                        }
                        setAssignmentDetailsDialog(false);
                      }}
                    >
                      Message Client
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Tab 1: Location */}
              {detailsTab === 1 && (
                <Box>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2, mb: 2 }}>
                      <LocationOn color="primary" sx={{ fontSize: 32 }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Site Address
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          {selectedAssignment.location?.address || 'Address not provided'}
                        </Typography>
                        {selectedAssignment.location?.area && (
                          <Chip label={selectedAssignment.location.area} size="small" />
                        )}
                        {selectedAssignment.location?.distance && (
                          <Chip
                            label={`${selectedAssignment.location.distance}km away`}
                            size="small"
                            sx={{ ml: 1 }}
                            color="primary"
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                      <Schedule color="primary" sx={{ fontSize: 32 }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Scheduled Time
                        </Typography>
                        <Typography variant="body1">
                          {selectedAssignment.scheduledDate || 'Not scheduled yet'}
                        </Typography>
                        {selectedAssignment.scheduledTime && (
                          <Typography variant="body2" color="text.secondary">
                            Time: {selectedAssignment.scheduledTime}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Contact Info */}
                  {(selectedAssignment.clientContact || selectedAssignment.contractorContact) && (
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Contact Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {selectedAssignment.clientContact && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Phone fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
                              Client:
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {selectedAssignment.clientContact}
                            </Typography>
                          </Box>
                        )}
                        {selectedAssignment.contractorContact && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Phone fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
                              Contractor:
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {selectedAssignment.contractorContact}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Tab 2: Requirements */}
              {detailsTab === 2 && (
                <Box>
                  {selectedAssignment.requirements && selectedAssignment.requirements.length > 0 ? (
                    <>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Verification Checklist
                      </Typography>
                      <List>
                        {selectedAssignment.requirements.map((req, index) => (
                          <ListItem key={index} sx={{ px: 0, py: 1.5 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <CheckBox color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary={req}
                              primaryTypographyProps={{ variant: 'body1' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Info sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="body1" color="text.secondary">
                        No specific requirements listed for this verification
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Tab 3: Documents */}
              {detailsTab === 3 && selectedAssignment.documents && selectedAssignment.documents.length > 0 && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Related Documents
                  </Typography>
                  <List>
                    {selectedAssignment.documents.map((doc, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          px: 0,
                          py: 2,
                          borderBottom: index < selectedAssignment.documents.length - 1 ? 1 : 0,
                          borderColor: 'divider'
                        }}
                        secondaryAction={
                          <Button
                            size="small"
                            startIcon={<FileDownload />}
                            variant="contained"
                          >
                            Download
                          </Button>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 48 }}>
                          <FileDownload color="primary" sx={{ fontSize: 32 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={doc}
                          primaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                          secondary="PDF Document"
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setAssignmentDetailsDialog(false)} variant="outlined">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Verification Start Dialog */}
      <Dialog
        open={verificationDialog}
        onClose={() => setVerificationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => setVerificationDialog(false)} />
        <DialogTitle>Start Verification</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Ready to start verification for:
          </Typography>
          <Typography variant="h6" gutterBottom>
            {selectedAssignment?.milestone}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {selectedAssignment?.projectTitle}
          </Typography>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            Ensure you have all required tools, documents, and safety equipment before starting.
          </Alert>
          
          <Alert severity="warning" sx={{ mt: 1 }}>
            Location: {selectedAssignment?.location?.address}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerificationDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<PlayArrow />}
            onClick={() => {
              setVerificationDialog(false);
              startVerification(selectedAssignment);
            }}
          >
            Start Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog
        open={filterDialog}
        onClose={() => setFilterDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => setFilterDialog(false)} />
        <DialogTitle>Filter Assignments</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="assigned">Assigned</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="submitted">Submitted (files)</MenuItem>
                  <MenuItem value="changes_requested">Changes Requested (files)</MenuItem>
                  <MenuItem value="disputed">Disputed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  label="Priority"
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value="high">High Priority</MenuItem>
                  <MenuItem value="normal">Normal Priority</MenuItem>
                  <MenuItem value="low">Low Priority</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  label="Location"
                >
                  <MenuItem value="all">All Locations</MenuItem>
                  {uniqueLocations.map((location) => (
                    <MenuItem key={location} value={location}>
                      {location}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearFilters}>Clear All</Button>
          <Button onClick={() => setFilterDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              // Persist the dialog's selections to the URL (also clears any
              // legacy tab param that would re-pin the status filter).
              syncFiltersToUrl(statusFilter, priorityFilter, locationFilter);
              setFilterDialog(false);
            }}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => handleStartVerification(filteredAssignments.find(a => a.status === 'assigned'))}
      >
        <CameraAlt />
      </Fab>
    </Container>
  );
};

export default VerifierAssignmentsPage; 
